/**
 * SentenceChunker
 *
 * Accumulates text tokens and emits complete sentences.
 * Used by iOS/Web streaming TTS where we need to feed sentence-by-sentence
 * to Azure Speech SDK (TextStream is not available on these platforms).
 *
 * Sentence boundary detection:
 * - Splits on . ! ? followed by whitespace
 * - Handles Spanish inverted marks ¡ ¿
 * - Handles common abbreviations (Mr., Mrs., Dr., Sr., Sra., etc.)
 * - Handles ellipsis (...)
 * - Minimum sentence length to avoid tiny fragments
 */

const MIN_SENTENCE_LENGTH = 10;

// Common abbreviations that should NOT trigger a sentence split.
const ABBREVIATIONS = new Set([
  'mr', 'mrs', 'ms', 'dr', 'prof', 'sr', 'sra', 'srta',
  'st', 'jr', 'etc', 'vs', 'approx', 'dept', 'est',
  'inc', 'ltd', 'co', 'corp',
  'jan', 'feb', 'mar', 'apr', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec',
  'fig', 'vol', 'no', 'ed', 'rev',
  'e.g', 'i.e',
]);

/**
 * Check if a period at position `dotIndex` in `text` is part of an abbreviation.
 */
function isAbbreviation(text: string, dotIndex: number): boolean {
  let start = dotIndex - 1;
  while (start >= 0 && /[a-zA-ZáéíóúñÁÉÍÓÚÑ.]/.test(text[start])) {
    start--;
  }
  const word = text.slice(start + 1, dotIndex).toLowerCase();
  return ABBREVIATIONS.has(word);
}

/**
 * Check if a period at position `dotIndex` is part of an ellipsis (...)
 */
function isEllipsis(text: string, dotIndex: number): boolean {
  const before = dotIndex > 0 && text[dotIndex - 1] === '.';
  const after = dotIndex < text.length - 1 && text[dotIndex + 1] === '.';
  return before || after;
}

export class SentenceChunker {
  private buffer = '';

  /**
   * Feed a text token. Returns an array of complete sentences detected
   * (usually 0 or 1, occasionally more if a token contains multiple boundaries).
   */
  feed(token: string): string[] {
    this.buffer += token;
    return this.extractSentences();
  }

  /**
   * Flush remaining buffered text (on stream end).
   * Returns the remaining text, or null if buffer is empty.
   */
  flush(): string | null {
    const remaining = this.buffer.trim();
    this.buffer = '';
    return remaining || null;
  }

  /**
   * Reset the chunker state.
   */
  reset(): void {
    this.buffer = '';
  }

  /**
   * Text accumulated but not yet emitted as a complete sentence.
   * Read-only view for consumers that need early-cut / cap decisions
   * (TtsSegmenter) — does not consume the buffer.
   */
  get pending(): string {
    return this.buffer;
  }

  /**
   * Force-cut the buffer at `endIndex` (inclusive): removes and returns
   * the trimmed prefix, keeping the rest buffered. Used by TtsSegmenter
   * for the first-clause early cut and the run-on length cap — sentence
   * boundary detection itself is untouched.
   */
  cutAt(endIndex: number): string {
    const cut = this.buffer.slice(0, endIndex + 1).trim();
    this.buffer = this.buffer.slice(endIndex + 1);
    return cut;
  }

  /**
   * Extract complete sentences from the buffer.
   * Finds sentence boundaries and splits on them, skipping boundaries
   * that would produce too-short sentences (merges them with the next).
   */
  private extractSentences(): string[] {
    const sentences: string[] = [];
    const boundaries = this.findAllBoundaries();

    if (boundaries.length === 0) return sentences;

    let consumed = 0;

    for (let i = 0; i < boundaries.length; i++) {
      const boundary = boundaries[i];
      const sentence = this.buffer.slice(consumed, boundary + 1).trim();

      if (sentence.length < MIN_SENTENCE_LENGTH && i < boundaries.length - 1) {
        // Too short — skip this boundary, merge with next sentence
        continue;
      }

      if (sentence.length < MIN_SENTENCE_LENGTH) {
        // Last boundary and still too short — don't split
        break;
      }

      sentences.push(sentence);
      consumed = boundary + 1;
    }

    if (consumed > 0) {
      this.buffer = this.buffer.slice(consumed);
    }

    return sentences;
  }

  /**
   * Find all valid sentence boundary positions in the buffer.
   * A boundary is a . ! ? (optionally followed by closing quotes/parens)
   * followed by whitespace.
   */
  private findAllBoundaries(): number[] {
    const boundaries: number[] = [];

    for (let i = 0; i < this.buffer.length; i++) {
      const char = this.buffer[i];

      if (char === '.' || char === '!' || char === '?') {
        // Skip closing quotes/parens that follow the punctuation
        let afterPunct = i + 1;
        while (afterPunct < this.buffer.length && /["'")\]»]/.test(this.buffer[afterPunct])) {
          afterPunct++;
        }

        const isAtEnd = afterPunct >= this.buffer.length;
        const isFollowedBySpace = afterPunct < this.buffer.length && /\s/.test(this.buffer[afterPunct]);

        if (!isAtEnd && !isFollowedBySpace) continue;

        // For periods: check abbreviations and ellipsis
        if (char === '.') {
          if (isAbbreviation(this.buffer, i)) continue;
          if (isEllipsis(this.buffer, i)) continue;
        }

        // Only split on boundaries followed by whitespace (not at end of buffer,
        // since more tokens may still arrive — flush() handles the final text)
        if (isAtEnd) continue;

        boundaries.push(afterPunct - 1);
      }
    }

    return boundaries;
  }
}
